import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { TrendingUp, Clock, X } from 'lucide-react';

const COLLECTION_WINDOW_MS = 48 * 60 * 60 * 1000;

const formatRemaining = remainingMs => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const MarketDataCollection = ({ pendingBuildings = [], onDismiss }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const visibleBuildings = useMemo(
    () => pendingBuildings.map(building => {
      const createdAt = new Date(building.createdAt || Date.now()).getTime();
      const endTime = createdAt + COLLECTION_WINDOW_MS;
      const remainingMs = Math.max(0, endTime - now);
      const elapsed = COLLECTION_WINDOW_MS - remainingMs;
      const progress = Math.min(
        100,
        Math.max(0, (elapsed / COLLECTION_WINDOW_MS) * 100),
      );

      return {
        ...building,
        remainingMs,
        timeRemaining: formatRemaining(remainingMs),
        progress: Math.round(progress),
      };
    }),
    [pendingBuildings, now],
  );

  if (visibleBuildings.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#E8F0F1',
        borderBottom: '1px solid #E5E7EB',
        padding: { xs: '1rem', md: '1.25rem' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'start', sm: 'center' },
          justifyContent: 'space-between',
          marginBottom: '1rem',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: '0.75rem', sm: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box
            sx={{
              width: '32px',
              height: '32px',
              borderRadius: '0.5rem',
              backgroundColor: '#4FA490',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrendingUp size={16} color='white' />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#111827',
              }}
            >
              Market Data Collection
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#6B7280',
              }}
            >
              Real-time analysis status
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.25rem 0.625rem',
            borderRadius: '9999px',
            backgroundColor: '#E8F0F1',
            border: '1px solid #4FA490',
          }}
        >
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: '#4FA490',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#4FA490',
            }}
          >
            {visibleBuildings.length} Active
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          maxHeight: '180px',
          overflowY: 'auto',
        }}
      >
        {visibleBuildings.map(building => (
          <Box
            key={building.id}
            sx={{
              padding: '0.75rem',
              borderRadius: '0.5rem',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'space-between',
                gap: '0.75rem',
                marginBottom: '0.5rem',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {building.address || building.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                  }}
                >
                  {building.totalUnits || 0} units • Analyzing comps
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.25rem',
                    backgroundColor: '#E8F0F1',
                  }}
                >
                  <Clock size={12} color='#4FA490' />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      color: '#4FA490',
                    }}
                  >
                    {building.timeRemaining}
                  </Typography>
                </Box>
                <IconButton
                  size='small'
                  onClick={() => onDismiss?.(building.id)}
                  sx={{
                    width: '24px',
                    height: '24px',
                    '&:hover': { backgroundColor: '#FEE2E2' },
                  }}
                >
                  <X size={16} color='#6B7280' />
                </IconButton>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                  }}
                >
                  Progress
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {building.progress}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#E5E7EB',
                  borderRadius: '9999px',
                  height: '6px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    backgroundColor: '#4FA490',
                    borderRadius: '9999px',
                    width: `${building.progress}%`,
                    transition: 'width 1s ease',
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MarketDataCollection;



